/**
 * @file store-utils.ts
 *
 * Shared building blocks for the shop admin zustand stores. Provides
 * {@link toMessage} (the error → string helper used by every store) and
 * {@link createFormSlice}, a configurable slice creator that standardises the
 * cache-first "load a seed, create, update, reset" form lifecycle shared by the
 * products, categories, and coupons stores. Composing this slice keeps the
 * per-entity stores to just their list state plus a few lines of wiring. The
 * slice also surfaces `formErrorCode` (the machine-readable {@link WcApiError}
 * code) so a form can map a known failure to a specific field — e.g. the coupons
 * form mapping a `duplicate_code` failure to the code field.
 */

import { WcApiError } from '../api/types';

/**
 * Coerces an unknown thrown value into a display string.
 *
 * @param err - The caught value (an {@link Error} or anything else).
 *
 * @returns The error message, or the value stringified.
 */
export function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Extracts the machine-readable error code from a {@link WcApiError}, or `null`
 * for any other thrown value. Lets a form map a known server failure to a field.
 *
 * @param err - The caught value.
 *
 * @returns The `WcApiError.code`, or `null`.
 */
export function toErrorCode(err: unknown): string | null {
  return err instanceof WcApiError ? err.code : null;
}

/** The seed/loading/error state every CRUD form needs. */
export interface FormSliceState<T> {
  /** The entity seeding the edit form, or `null` when creating. */
  current: T | null;
  /** Whether {@link FormSliceActions.loadOne} is fetching a seed entity. */
  formLoading: boolean;
  /** Error from loading or saving the entity (display message), or `null`. */
  formError: string | null;
  /** Machine-readable code of the last failure (e.g. `"duplicate_code"`), or `null`. */
  formErrorCode: string | null;
}

/**
 * The CRUD-form actions every entity form needs. `I` is the entity id type — the
 * catalog (products, categories) keys by UUID `string`; coupons and the other
 * WooCommerce-style domains key by `number` (the default).
 */
export interface FormSliceActions<T, P, I = number> {
  /**
   * Loads a single entity to seed the form. Returns the already-loaded list
   * copy synchronously when present (instant from the list); otherwise fetches
   * by id (a direct link or refresh). The result is stored on `current`.
   *
   * @param id - The entity id to load.
   *
   * @returns The entity, or `null` when the fetch fails.
   */
  loadOne: (id: I) => Promise<T | null>;

  /**
   * Creates an entity.
   *
   * @param payload - The entity write payload.
   *
   * @returns The created entity, or `null` when the request fails.
   */
  submitCreate: (payload: P) => Promise<T | null>;

  /**
   * Updates an entity.
   *
   * @param id - The entity id to update.
   * @param payload - The entity write payload.
   *
   * @returns The updated entity, or `null` when the request fails.
   */
  submitUpdate: (id: I, payload: P) => Promise<T | null>;

  /** Clears the form seed/loading/error state when leaving the form. */
  resetForm: () => void;
}

/** The full CRUD-form slice: state plus actions, keyed by id type `I` (default `number`). */
export type FormSlice<T, P, I = number> = FormSliceState<T> &
  FormSliceActions<T, P, I>;

/**
 * Setter restricted to the form-slice keys. A composed store's devtools `set`
 * satisfies this (its `Partial<WholeStore>` parameter accepts the narrower
 * `Partial<FormSliceState<T>>`), so the slice writes only its own keys without
 * needing to know the surrounding store shape.
 */
export type FormSet<T> = (
  partial: Partial<FormSliceState<T>>,
  replace?: false,
  action?: string,
) => void;

/** Configuration for {@link createFormSlice}. `I` is the entity id type (default `number`). */
export interface FormSliceConfig<S, T, P, R, I = number> {
  /** Devtools action-label prefix, e.g. `"products"` → `"products/loadOne/cache"`. */
  name: string;
  /** Fetches the raw entity by id (used when not present in the cached list). */
  fetchById: (id: I) => Promise<R>;
  /** Creates the entity and returns the raw result. */
  create: (payload: P) => Promise<R>;
  /** Updates the entity and returns the raw result. */
  update: (id: I, payload: P) => Promise<R>;
  /** Reads the cached entity from the composed store's list state, if any. */
  selectCached: (get: () => S) => (id: I) => T | undefined;
  /** Maps a raw API result to the UI shape. Defaults to identity (when `R` is `T`). */
  normalize?: (raw: R) => T;
}

/**
 * Builds a reusable zustand slice implementing the cache-first CRUD-form
 * lifecycle. Compose it into a store by spreading its result:
 *
 * ```ts
 * create<ProductsState>()(devtools((set, get) => ({
 *   ...listState,
 *   ...createFormSlice<ProductsState, ShopProduct, ShopProductPayload>({ ... })(set, get),
 * })));
 * ```
 *
 * @typeParam S - The composed store's full state (so `selectCached` can read it).
 * @typeParam T - The UI entity type stored on `current`.
 * @typeParam P - The write payload type for create/update.
 * @typeParam R - The raw API result type; defaults to `T`.
 * @typeParam I - The entity id type; defaults to `number`.
 *
 * @param config - Wiring for the entity's API calls, cache lookup, and devtools name.
 *
 * @returns A slice creator `(set, get) => FormSlice<T, P, I>` ready to spread into a store.
 */
export function createFormSlice<S extends FormSlice<T, P, I>, T, P, R = T, I = number>(
  config: FormSliceConfig<S, T, P, R, I>,
): (set: FormSet<T>, get: () => S) => FormSlice<T, P, I> {
  // Identity default is only sound when the caller leaves `R` as `T`; the cast
  // bridges the generic `R`/`T` gap that TypeScript can't see is the same here.
  const normalize = config.normalize ?? ((raw: R) => raw as unknown as T);

  return (set, get) => ({
    current: null,
    formLoading: false,
    formError: null,
    formErrorCode: null,

    loadOne: async (id) => {
      const cached = config.selectCached(get)(id);
      if (cached) {
        set(
          { current: cached, formLoading: false, formError: null, formErrorCode: null },
          false,
          `${config.name}/loadOne/cache`,
        );
        return cached;
      }
      set(
        { current: null, formLoading: true, formError: null, formErrorCode: null },
        false,
        `${config.name}/loadOne/pending`,
      );
      try {
        const entity = normalize(await config.fetchById(id));
        set({ current: entity, formLoading: false }, false, `${config.name}/loadOne/fulfilled`);
        return entity;
      } catch (err) {
        set(
          { formLoading: false, formError: toMessage(err), formErrorCode: toErrorCode(err) },
          false,
          `${config.name}/loadOne/rejected`,
        );
        return null;
      }
    },

    submitCreate: async (payload) => {
      set({ formError: null, formErrorCode: null }, false, `${config.name}/create/pending`);
      try {
        return normalize(await config.create(payload));
      } catch (err) {
        set(
          { formError: toMessage(err), formErrorCode: toErrorCode(err) },
          false,
          `${config.name}/create/rejected`,
        );
        return null;
      }
    },

    submitUpdate: async (id, payload) => {
      set({ formError: null, formErrorCode: null }, false, `${config.name}/update/pending`);
      try {
        return normalize(await config.update(id, payload));
      } catch (err) {
        set(
          { formError: toMessage(err), formErrorCode: toErrorCode(err) },
          false,
          `${config.name}/update/rejected`,
        );
        return null;
      }
    },

    resetForm: () => {
      set(
        { current: null, formLoading: false, formError: null, formErrorCode: null },
        false,
        `${config.name}/resetForm`,
      );
    },
  });
}
